import { useTranslations } from '@/hooks/useTranslations';
import { cn } from '@/lib/utils';
import { Link } from '@inertiajs/react';
import { BookOpen, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { route } from 'ziggy-js';

/**
 * Floating, gently pulsating reminder that nudges users toward the CRM
 * knowledge base. Desktop only. Dismissible via the ✕, but it re-appears
 * after REAPPEAR_AFTER_MS so it keeps acting as a low-key reminder.
 */
const STORAGE_KEY = 'kb-crm-bubble-dismissed-at';
const REAPPEAR_AFTER_MS = 4 * 60 * 60 * 1000; // 4 hours

export function KnowledgeBaseBubble() {
    const { t } = useTranslations();
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const dismissedAt = Number(window.localStorage.getItem(STORAGE_KEY));
        const stillDismissed = Number.isFinite(dismissedAt) && dismissedAt > 0 && Date.now() - dismissedAt < REAPPEAR_AFTER_MS;

        setVisible(!stillDismissed);
    }, []);

    if (!visible) {
        return null;
    }

    const handleDismiss = () => {
        if (typeof window !== 'undefined') {
            window.localStorage.setItem(STORAGE_KEY, String(Date.now()));
        }
        setVisible(false);
    };

    const label = t('navigation.kb_bubble.label', 'How to use the CRM');

    return (
        <div className="fixed right-6 bottom-6 z-50 hidden md:block">
            <div className="group relative">
                {/* Dismiss button */}
                <button
                    type="button"
                    onClick={handleDismiss}
                    aria-label={t('navigation.kb_bubble.dismiss', 'Dismiss')}
                    className="border-border bg-background text-muted-foreground hover:text-foreground focus-visible:ring-ring absolute -top-1 -right-1 z-10 flex size-5 items-center justify-center rounded-full border opacity-0 shadow-sm transition-opacity duration-200 group-hover:opacity-100 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:outline-none"
                >
                    <X className="size-3" />
                </button>

                <Link
                    href={`${route('knowledge-base.index')}?category=crm`}
                    aria-label={t('navigation.kb_bubble.aria_label', 'Open the CRM knowledge base')}
                    className={cn(
                        'bg-primary text-primary-foreground flex h-14 items-center overflow-hidden rounded-full shadow-lg',
                        'transition-[width,padding] duration-300 ease-out',
                        'w-14 group-hover:w-auto group-hover:pr-5',
                        'focus-visible:ring-ring focus-visible:ring-offset-background focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
                    )}
                >
                    {/* Pulsing halo — sits behind the solid icon disc so the icon stays crisp */}
                    <span className="relative flex size-14 shrink-0 items-center justify-center">
                        <span className="animate-kb-pulse bg-primary absolute inline-flex size-12 rounded-full motion-reduce:animate-none" />
                        <BookOpen className="relative size-6" />
                    </span>

                    <span className="max-w-0 text-sm font-medium whitespace-nowrap opacity-0 transition-[max-width,opacity] duration-300 ease-out group-hover:max-w-xs group-hover:opacity-100">
                        {label}
                    </span>
                </Link>
            </div>
        </div>
    );
}
